import { computed, inject, Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, Observable, of, switchMap } from 'rxjs';

import { AppState } from '@store';
import { buildNavigationItems, canReachRoute } from './navigation-items';

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

const whenBrowserIdle = () =>
  new Observable<void>((subscriber) => {
    const done = () => {
      subscriber.next();
      subscriber.complete();
    };

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(done, { timeout: 3000 });

      return () => cancelIdleCallback(handle);
    }

    const handle = setTimeout(done, 1000);

    return () => clearTimeout(handle);
  });

const connectionAllowsPreloading = () => {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection;

  return !connection?.saveData && !connection?.effectiveType?.endsWith('2g');
};

@Injectable({ providedIn: 'root' })
export class IdlePreloadingStrategy implements PreloadingStrategy {
  store = inject<Store<AppState>>(Store);

  isLoggedIn = this.store.selectSignal((state) => state.auth.isLoggedIn);
  userBean = this.store.selectSignal((state) => state.user);
  community = this.store.selectSignal((state) => state.community);
  root = this.store.selectSignal((state) => state.root);

  canReach = computed(() =>
    canReachRoute(
      buildNavigationItems({
        userBean: this.userBean(),
        community: this.community(),
        root: this.root(),
      })
    )
  );

  preload = (route: Route, load: () => Observable<unknown>) =>
    this.isLoggedIn() && this.canReach()(route) && connectionAllowsPreloading()
      ? whenBrowserIdle().pipe(
          switchMap(load),
          catchError(() => of(null))
        )
      : EMPTY;
}
